import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Room } from '../../../shared/types/Room';
import { MoreHorizontalIcon, PlusIcon, EditIcon, ImageIcon, TrashIcon } from 'lucide-react';
export function Rooms() {
  const {
    t
  } = useTranslation('rooms');
  // Mock data based on the screenshot
  const [rooms, setRooms] = useState<Room[]>([{
    id: '1',
    name: 'Scuba Spa Test',
    code: 'Scuba Spa Test',
    category: 'SUPERIOR',
    status: 'inactive',
    image: ''
  }, {
    id: '2',
    name: 'Deluxe Beach Villa',
    code: 'DBV',
    category: 'DELUXE',
    status: 'inactive',
    image: ''
  }, {
    id: '3',
    name: 'Water Villa',
    code: 'SUNSIYAMEX',
    category: 'OVERWATER_VILLA',
    status: 'active',
    image: 'https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/akmkk18tu8vmgxet3nfyip/2db2784c-a6fd-4102-a1f3-1a3d11f02729/Screenshot_2025-10-01_at_10.57.35_AM.png'
  }, {
    id: '4',
    name: 'Premium Overwater Villa',
    code: 'POWVDTAC',
    category: 'OVERWATER_VILLA',
    status: 'active',
    image: 'https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/akmkk18tu8vmgxet3nfyip/2db2784c-a6fd-4102-a1f3-1a3d11f02729/Screenshot_2025-10-01_at_10.57.42_AM.png'
  }]);
  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  // Action menu state
  const [actionMenuOpen, setActionMenuOpen] = useState<string | null>(null);
  // Filter rooms based on search term and filters
  const filteredRooms = rooms.filter(room => {
    const matchesSearch = room.name.toLowerCase().includes(searchTerm.toLowerCase()) || room.code.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || statusFilter === 'active' && room.status === 'active' || statusFilter === 'inactive' && room.status === 'inactive';
    const matchesCategory = categoryFilter === 'all' || formatCategoryName(room.category) === categoryFilter;
    return matchesSearch && matchesStatus && matchesCategory;
  });
  // Format category name for display
  function formatCategoryName(category: string): string {
    return category.replace('_', ' ');
  }
  // Navigate to add room page
  const navigateToAddRoom = () => {
    window.location.href = '/add-room';
  };
  // Navigate to edit room page
  const navigateToEditRoom = (roomId: string) => {
    window.location.href = `/edit-room?id=${roomId}`;
  };
  // Navigate to room assets page
  const navigateToRoomAssets = (roomId: string) => {
    window.location.href = `/room-detail?id=${roomId}`;
  };
  // Delete room
  const deleteRoom = (roomId: string) => {
    if (confirm('Are you sure you want to delete this room?')) {
      setRooms(rooms.filter(room => room.id !== roomId));
    }
  };
  // Toggle action menu
  const toggleActionMenu = (roomId: string) => {
    if (actionMenuOpen === roomId) {
      setActionMenuOpen(null);
    } else {
      setActionMenuOpen(roomId);
    }
  };
  return <div className="space-y-6" data-id="element-10567">
      <div className="flex justify-between items-center" data-id="element-10568">
        <h1 className="text-2xl font-semibold text-gray-800" data-id="element-10569">
          {t('pages.Rooms.rooms')}
        </h1>
        <button onClick={navigateToAddRoom} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primaryhover)] text-[#1A1A1A] px-4 py-2 rounded-md flex items-center" data-id="element-10570">
          <PlusIcon size={18} className="mr-1" data-id="element-10571" />
          {t('components.AddRoomForm.add_room')}
        </button>
      </div>
      {/* Filters */}
      <div className="flex flex-wrap gap-3" data-id="element-10572">
        <input type="search" aria-label={t('pages.Rooms.attrs.search_rooms')} placeholder={t('pages.Rooms.attrs.search_rooms')} className="border border-gray-300 rounded-md px-3 py-2 w-48" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-10573" />

        <select className="border border-gray-300 rounded-md px-3 py-2" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} data-id="element-10574">
          <option value="all" data-id="element-10575">{t('components.AddRoomForm.status')}</option>
          <option value="active" data-id="element-10576">{t('pages.Rates.active')}</option>
          <option value="inactive" data-id="element-10577">{t('pages.Rates.inactive')}</option>
        </select>
        <select className="border border-gray-300 rounded-md px-3 py-2" value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)} data-id="element-10578">
          <option value="all" data-id="element-10579">{t('pages.Rooms.room_category')}</option>
          <option value="SUPERIOR" data-id="element-10580">{t('pages.Rates.superior')}</option>
          <option value="DELUXE" data-id="element-10581">{t('pages.Rates.deluxe')}</option>
          <option value="OVERWATER VILLA" data-id="element-10582">
            {t('pages.Rates.overwater_villa')}
          </option>
        </select>
      </div>
      {/* Room List */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden" data-id="element-10583">
        <table className="min-w-full divide-y divide-gray-200" data-id="element-10584">
          <thead className="bg-gray-50" data-id="element-10585">
            <tr data-id="element-10586">
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10587">
                {t('components.RoomAssets.room_image')}
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10588">
                {t('components.AddRoomForm.room_name')}
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10589">
                {t('components.AddRoomForm.room_category')}
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10590">
                {t('components.AddRoomForm.room_code')}
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10591">
                {t('components.AddRoomForm.status')}
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10592">
                {t('components.RoomAssets.actions')}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200" data-id="element-10593">
            {filteredRooms.map(room => <tr key={room.id} data-id="element-10594">
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-10595">
                  {room.image ? <img src={room.image} alt={room.name} className="h-16 w-24 object-cover rounded" data-id="element-10596" /> : <div className="h-16 w-24 bg-gray-100 flex items-center justify-center text-gray-400 text-xs rounded" data-id="element-10597">
                      {t('pages.Rooms.no_image')}
                    </div>}
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-10598">
                  <div className="text-sm font-medium text-gray-900" data-id="element-10599">
                    {room.name}
                  </div>
                  <div className="text-sm text-gray-500" data-id="element-10600">{room.code}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-10601">
                  <div className="text-sm text-gray-900" data-id="element-10602">
                    {formatCategoryName(room.category)}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-10603">
                  <div className="text-sm text-gray-900" data-id="element-10604">{room.code}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-10605">
                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${room.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-10606">
                    {room.status === 'active' ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium relative" data-id="element-10607">
                  <button aria-label="Room actions" aria-expanded={actionMenuOpen === room.id} className="text-gray-400 hover:text-gray-500" onClick={() => toggleActionMenu(room.id)} data-id="element-10608">
                    <MoreHorizontalIcon size={18} aria-hidden="true" data-id="element-10609" />
                  </button>
                  {actionMenuOpen === room.id && <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10" data-id="element-10610">
                      <div className="py-1" role="menu" aria-orientation="vertical" data-id="element-10611">
                        <button onClick={() => {
                    setActionMenuOpen(null);
                    navigateToEditRoom(room.id);
                  }} className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left" data-id="element-10612">
                          <EditIcon size={16} className="mr-2" data-id="element-10613" />
                          {t('pages.Rooms.edit')}
                        </button>
                        <button onClick={() => {
                    setActionMenuOpen(null);
                    navigateToRoomAssets(room.id);
                  }} className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left" data-id="element-10614">
                          <ImageIcon size={16} className="mr-2" data-id="element-10615" />
                          {t('pages.Rooms.manage_images')}
                        </button>
                        <button onClick={() => {
                    setActionMenuOpen(null);
                    deleteRoom(room.id);
                  }} className="flex items-center px-4 py-2 text-sm text-red-600 hover:bg-gray-100 w-full text-left" data-id="element-10616">
                          <TrashIcon size={16} className="mr-2" data-id="element-10617" />
                          {t('pages.Rooms.delete')}
                        </button>
                      </div>
                    </div>}
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>
    </div>;
}